/**
 * A popular Intertown route's town radius, for the admin Routes editor. node --test
 *
 * The radius says how big the town is: a rider's own spot within it of the town centre counts as
 * inside the town (so it gets the route's fixed fare), and it is how close an Anna must be to mark
 * "arrived" when the drop is the town itself. The server clamps and defaults it the same way
 * (firebase/functions/intertown.js, normaliseConfig), and the rider app reads it the same way
 * (passenger/src/utils/intertownPopular.js); a test compares all three.
 */

export const DEFAULT_TOWN_RADIUS_KM = 8;
export const MIN_TOWN_RADIUS_KM = 1;
export const MAX_TOWN_RADIUS_KM = 25;

/**
 * The radius to save for a route: what was typed, kept between 1 and 25 km. Blank, zero or anything
 * that is not a number means the default of 8 km, exactly as every route has been saved until now.
 */
export const normaliseTownRadius = (value) => (
  Math.max(MIN_TOWN_RADIUS_KM, Math.min(MAX_TOWN_RADIUS_KM, Number(value) || DEFAULT_TOWN_RADIUS_KM))
);

/**
 * Whether what is in the box can be saved. Blank is fine (it means the default). Anything typed must
 * be a number from 1 to 25, so a slip like 80 is refused with a message instead of quietly becoming 25.
 */
export const hasValidTownRadius = (value) => {
  if (value === undefined || value === null || String(value).trim() === '') return true;
  const km = Number(value);
  return Number.isFinite(km) && km >= MIN_TOWN_RADIUS_KM && km <= MAX_TOWN_RADIUS_KM;
};

/**
 * A route's town centre: the point the server measures the town radius from, so it can charge the
 * route's fixed fare to any spot inside that town itself (an older or altered app cannot get the
 * cheaper per-km price for the same trip). The admin pastes what Google Maps copies when you
 * right-click a place: "13.6288, 79.4192". The same India-only box the server holds every place to
 * (firebase/functions/intertown.js townCentreOf); blank means "not set" and changes nothing.
 */
const round6 = (n) => Math.round(n * 1e6) / 1e6;

/** "13.6288, 79.4192" (comma, semicolon or just a space between) as { lat, lng }, or null for blank or unusable text. */
export const parseTownCentre = (text) => {
  const match = String(text ?? '').trim().match(/^(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)$/);
  if (!match) return null;
  const lat = Number(match[1]);
  const lng = Number(match[2]);
  if (!(lat >= 5 && lat <= 38 && lng >= 68 && lng <= 98)) return null;
  return { lat: round6(lat), lng: round6(lng) };
};

/** Whether what is in the box can be saved: blank (not set) or a real point in India. */
export const hasValidTownCentre = (text) => (
  text === undefined || text === null || String(text).trim() === '' || parseTownCentre(text) !== null
);

/** The saved centre of a route as the text the box shows, or '' when it has none. */
export const formatTownCentre = (route) => {
  const lat = Number(route?.centerLat);
  const lng = Number(route?.centerLng);
  const set = route?.centerLat !== null && route?.centerLat !== undefined && route?.centerLat !== ''
    && route?.centerLng !== null && route?.centerLng !== undefined && route?.centerLng !== '';
  return set && Number.isFinite(lat) && Number.isFinite(lng) ? `${lat}, ${lng}` : '';
};

/**
 * The centre to save for a route: what is typed in the box when the admin has touched it, otherwise
 * the centre already saved. Null when there is none (or what was typed is unusable, which the save
 * refuses before it gets here).
 */
export const townCentreFor = (route) => (
  route?.townCentre !== undefined ? parseTownCentre(route.townCentre) : parseTownCentre(formatTownCentre(route))
);

